import { CurrentUser } from "./models";

// Read-only view of AuthService used by the route guards. AuthService
// satisfies this structurally; keeping the decision logic dependent only on
// this interface lets the guard rules be unit-tested without an Angular
// runtime (see auth.guard.logic.spec.ts).
export interface AuthView {
  user(): CurrentUser | null;
  isClient(): boolean;
  isOffice(): boolean;
  isAdmin(): boolean;
  has(permission: string): boolean;
}

// A guard decision: either allow (`true`) or redirect to a route path.
export type GuardDecision = true | { redirectTo: string };

// Protected office/crew routes. Logged-out → /login; clients are a separate
// principal and bounced to /portal; permission-scoped routes fall back to /my
// when the user lacks the required permission/role.
export function decideAuthGuard(
  auth: AuthView,
  need: string | undefined,
): GuardDecision {
  if (!auth.user()) return { redirectTo: "/login" };
  if (auth.isClient()) return { redirectTo: "/portal" };
  if (need === "office" && !auth.isOffice()) return { redirectTo: "/my" };
  if (need === "admin" && !auth.isAdmin()) return { redirectTo: "/my" };
  if (need && need !== "office" && need !== "admin" && !auth.has(need))
    return { redirectTo: "/my" };
  return true;
}

// Customer-portal routes: clients only. Logged-out → /login; any employee → /dashboard.
export function decidePortalGuard(auth: AuthView): GuardDecision {
  if (!auth.user()) return { redirectTo: "/login" };
  if (!auth.isClient()) return { redirectTo: "/dashboard" };
  return true;
}

// Landing / login / signup: logged-out visitors stay; logged-in users are sent
// to their workspace (clients → /portal, everyone else → /dashboard).
export function decideLoginGuard(auth: AuthView): GuardDecision {
  if (!auth.user()) return true;
  return { redirectTo: auth.isClient() ? "/portal" : "/dashboard" };
}
